import React from 'react';
import {useLocation} from 'react-router-dom';

import {text} from '../text';
import {hooks} from '../hooks';
import {custom} from '../custom';
import {svg} from '../assets/svg';
import {theme} from '../constants';
import {components} from '../components';
import {useLanguage} from '../context/LanguageContext';
import axios from 'axios';

export const ResetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = hooks.useAppNavigate();
  const {t} = useLanguage();
  const rp = (t as any).resetPassword;

  const token = new URLSearchParams(location.search).get('token') || '';

  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    if (!token) setError(rp?.invalidToken || 'Invalid reset link.');
  }, [token]);

  const handleSubmit = async () => {
    if (password.length < 8) { setError(rp?.minLength); return; }
    if (password !== confirmPassword) { setError(rp?.mismatch); return; }
    try {
      setIsLoading(true);
      setError('');
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
      await axios.post(`${apiBase}/auth/reset-password`, {token, password});
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.message || rp?.invalidToken);
    } finally {
      setIsLoading(false);
    }
  };

  const renderBackground = (): JSX.Element => <components.Background />;

  const renderHeader = () => (
    <components.Header title={rp?.title || 'Create New Password'} goBack={false} />
  );

  const renderContent = () => {
    if (done) {
      return (
        <main style={{padding: '30px 20px 20px 20px', marginTop: 52}}>
          <div style={{textAlign: 'center', marginBottom: 24}}>
            <div style={{fontSize: 48, marginBottom: 12}}>✓</div>
            <text.T16 style={{color: theme.colors.textColor, lineHeight: 1.5}}>
              {rp?.success}
            </text.T16>
          </div>
          <components.Button
            title={(t as any).forgotPassword?.backToLogin || 'Back to Login'}
            onClick={() => navigate('/SignIn')}
          />
        </main>
      );
    }

    return (
      <main style={{padding: '30px 20px 20px 20px', marginTop: 52}}>
        <text.T16 style={{marginBottom: 24, color: theme.colors.textColor, lineHeight: 1.5}}>
          {rp?.subtitle}
        </text.T16>
        {error ? (
          <div
            style={{
              backgroundColor: '#FFE0E0',
              color: '#C00000',
              padding: '10px 15px',
              borderRadius: 8,
              marginBottom: 14,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        ) : null}
        <custom.InputField
          value={password}
          onChange={(e: any) => setPassword(e.target.value)}
          placeholder={rp?.newPassword || 'New Password'}
          leftIcon={<svg.PasswordSvg />}
          rightIcon={<svg.EyeOffSvg />}
          isPasswordField={true}
          containerStyle={{marginBottom: 10}}
        />
        <custom.InputField
          value={confirmPassword}
          onChange={(e: any) => setConfirmPassword(e.target.value)}
          placeholder={rp?.confirmPassword || 'Confirm Password'}
          leftIcon={<svg.PasswordSvg />}
          rightIcon={<svg.EyeOffSvg />}
          isPasswordField={true}
          containerStyle={{marginBottom: 14}}
        />
        <components.Button
          title={isLoading ? (rp?.submitting || 'Saving...') : (rp?.submit || 'Save Password')}
          onClick={handleSubmit}
        />
      </main>
    );
  };

  return (
    <div id='screen'>
      {renderBackground()}
      {renderHeader()}
      {renderContent()}
    </div>
  );
};
